import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getJobPostList } from '../../../../utils/api_service/JobPosting';
import { RootState } from '../../../../utils/redux_store/store';
import type { FilterState, JobPostingData } from '../../../../utils/types/JobOpenings';
import { DEFAULT_JOB_POST_VIEW_MODE, EMPTY_JOB_FILTERS, JOB_POST_PAGE_LIMIT } from '../constants';
import { countActiveFilters } from '../functions';
import type { JobPostViewMode } from '../interfaces';

export interface IUseJobPostTable {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  search: boolean;
  setSearch: (value: boolean) => void;
  pageNumber: number;
  setPageNumber: (page: number) => void;
  jobPostData: JobPostingData[];
  viewMode: JobPostViewMode;
  setViewMode: (mode: JobPostViewMode) => void;
  isLoading: boolean;
  maxPages: number;
  activeFilterCount: number;
  companyLogo?: string | null;
  companyName?: string | null;
  scrollGridRef: React.RefObject<HTMLDivElement>;
  handleClearAllFilters: () => void;
}

export const useJobPostTable = (): IUseJobPostTable => {
  const [filters, setFilters] = useState<FilterState>(EMPTY_JOB_FILTERS);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [search, setSearch] = useState<boolean>(true);
  const [jobPostData, setJobPostData] = useState<JobPostingData[]>([]);
  const [viewMode, setViewMode] = useState<JobPostViewMode>(DEFAULT_JOB_POST_VIEW_MODE);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [maxPages, setMaxPages] = useState<number>(1);
  const { companyLogo, companyName } = useSelector((state: RootState) => state.logo);

  const activeFilterCount = countActiveFilters(filters);
  const scrollGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchJobPost = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await getJobPostList(
          filters.jobTitle,
          filters.location,
          filters.department,
          filters.workType,
          pageNumber,
          JOB_POST_PAGE_LIMIT,
        );
        const responseData = await response.json();
        setJobPostData(responseData.results);
        setMaxPages(Math.ceil(responseData.total / JOB_POST_PAGE_LIMIT));
      } catch (error) {
        console.error('Error fetching job post:', error);
        setJobPostData([]);
        setMaxPages(1);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchJobPost();
  }, [search, pageNumber, filters]);

  useEffect(() => {
    if (scrollGridRef.current) {
      scrollGridRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pageNumber, search, filters]);

  const handleClearAllFilters = (): void => {
    setFilters(EMPTY_JOB_FILTERS);
    setSearch(!search);
  };

  return {
    filters,
    setFilters,
    search,
    setSearch,
    pageNumber,
    setPageNumber,
    jobPostData,
    viewMode,
    setViewMode,
    isLoading,
    maxPages,
    activeFilterCount,
    companyLogo,
    companyName,
    scrollGridRef,
    handleClearAllFilters,
  };
};
